import { computed } from '@vue/composition-api';
import { Store } from 'vuex';
import {
    ExtractGetterTypes,
    ExtractTypes,
    getStoreFromInstance,
    KnownKeys, RefTypes, useMapping,
} from 'vuex-composition-helpers/src/util';

import {
    Nullish,
    useState,
    useActions,
    useMutations,
    useGetters,
    useNamespacedState,
    useNamespacedGetters,
    useNamespacedActions,
    useNamespacedMutations,
} from 'vuex-composition-helpers/src/index';

export {
    useState,
    useActions,
    useMutations,
    useGetters,
    useNamespacedState,
    useNamespacedGetters,
    useNamespacedActions,
    useNamespacedMutations,
} from 'vuex-composition-helpers/src/index';

export function getField(state: any) {
    return (path: string) => path.split(/[.[\]]+/).reduce((prev, key) => prev[key], state);
}

export function updateField(state: any, { path, value }: { path: string, value: string}) {
    path.split(/[.[\]]+/).reduce((prev, key, index, array) => {
        if (array.length === index + 1) {
            // eslint-disable-next-line no-param-reassign
            prev[key] = value;
        }

        return prev[key];
    }, state);
}

export function computedGetter<T = any>(store: any, getterType: string, prop: string) {
    return computed<T>(() => store.getters[getterType](prop));
}

export function getMutation(store: any, mutationType: string): Function {
    return (...args: [any]) => store.commit.apply(store, [mutationType, ...args]);
}

export function useFields<TGetters = any>(storeOrMap: Store<any> | KnownKeys<TGetters>[], map?: KnownKeys<TGetters>[]): ExtractGetterTypes<TGetters> {
    let store = storeOrMap;
    if (arguments.length === 1) {
        map = store as KnownKeys<TGetters>[];
        store = getStoreFromInstance();
    }
    return useMapping(store, null, map, <T = any>($store: any, prop: string) => {
        const getter = computedGetter<T>($store, 'getField', prop);
        const setter = getMutation($store, 'updateField');
        return computed({
            get() {
                return getter.value;
            },
            set(value: any) {
                setter({ path: prop, value });
            },
        });
    });
}
/**
 *
 * import useStore, { useNamespaceFields } from '@/lib/vuexHelper';
 * const { project } = useNamespaceFields('user', ['project']);
 * project.value = '123';
 *
 * or use object
 * const { project_id } = useNamespaceFields('user', { project_id: 'projectId' });
 * project_id.value = '123';
 *
 */
export function useNamespaceFields<TGetters = any>(storeOrNamespace: Store<any> | string | Nullish, namespaceOrMap: KnownKeys<TGetters> | KnownKeys<TGetters>[] | Array<string> | string, map?: KnownKeys<TGetters>[]): ExtractGetterTypes<TGetters> {
    let store: Store<any>; let
        namespace: string;

    if (arguments.length === 2) {
        store = getStoreFromInstance();
        map = namespaceOrMap as KnownKeys<TGetters>[];
        namespace = storeOrNamespace as string;
    } else {
        store = storeOrNamespace as Store<any> || getStoreFromInstance();
        namespace = namespaceOrMap as string;
    }
    if (namespace.length && namespace.charAt(namespace.length - 1) !== '/') {
        namespace += '/';
    }
    return useMapping(store, null, map, <T = any>($store: any, prop: string) => {
        const getter = computedGetter<T>($store, `${namespace}getField`, prop);
        const setter = getMutation($store, `${namespace}updateField`);
        return computed({
            get() {
                return getter.value;
            },
            set(value: any) {
                setter({ path: prop, value });
            },
        });
    });
}

export function createNamespacedHelpers<TState = any, TGetters = any, TActions = any, TMutations = any>(storeOrNamespace: Store<any> | string, namespace?: string):{
    useState: (map?: KnownKeys<TState>[]) => RefTypes<TState>;
    useGetters: (map?: KnownKeys<TGetters>[]) => ExtractGetterTypes<TGetters>;
    useMutations: (map?: KnownKeys<TMutations>[]) => ExtractTypes<TMutations, Function>;
    useActions: (map?: KnownKeys<TActions>[]) => ExtractTypes<TActions, Function>;
    useFields: (map?: KnownKeys<TGetters>[]) => ExtractGetterTypes<TGetters>;
} {
    let store: Store<any> | Nullish;
    if (arguments.length === 1) {
        namespace = storeOrNamespace as string;
    } else {
        store = storeOrNamespace as Store<any>;
        if (!namespace) {
            throw new Error('Namespace is missing to provide namespaced helpers');
        }
    }
    return {
        useState: (map?: KnownKeys<TState>[]) => useNamespacedState(store, namespace as string, map),
        useGetters: (map?: KnownKeys<TGetters>[]) => useNamespacedGetters(store, namespace as string, map),
        useMutations: (map?: KnownKeys<TMutations>[]) => useNamespacedMutations(store, namespace as string, map),
        useActions: (map?: KnownKeys<TActions>[]) => useNamespacedActions(store, namespace as string, map),
        useFields: (map?: KnownKeys<TGetters>[]) => useNamespaceFields(store, namespace as string, map),
    };
}

export function wrapStore(store: any) {
    return {
        createNamespacedHelpers: createNamespacedHelpers.bind(null, store),
        useActions: useActions.bind(null, store),
        useGetters: useGetters.bind(null, store),
        useMutations: useMutations.bind(null, store),
        useState: useState.bind(null, store),
        useFields: useFields.bind(null, store),
    };
}
